/** The video job writes the project name and the video filename into its PDF report with a
 * latin-1 font (fpdf 1.7.2 in biodroneportal). Any other character fails the job at its last step. */

const UNSUPPORTED = /[^\x20-\x7E\xA0-\xFF]/u;
const UNSUPPORTED_RUNS = /[^\x20-\x7E\xA0-\xFF]+/gu;

/** The characters of `text` the report cannot write, each once, in order of appearance. */
export const unsupportedReportChars = text => [
	...new Set([...String(text ?? '').normalize('NFC')].filter(ch => UNSUPPORTED.test(ch))),
];

/** "：" → `"：" (U+FF1A)`, so a look-alike of a normal character can be told apart. */
export const describeChar = ch =>
	`"${ch}" (U+${ch.codePointAt(0).toString(16).toUpperCase().padStart(4, '0')})`;

/** Typographic punctuation NFKC leaves as it is. */
const PLAIN_PUNCTUATION = { '‘': "'", '’': "'", '“': '"', '”': '"', '–': '-', '—': '-' };

/** An unsupported character's plain form ("：" → ":", "ｆ" → "f"), or "_" when it has none. Only
 * unsupported characters go through NFKC: it would also change supported ones ("µ" → "μ"). A
 * look-alike slash ("／", "＼") also becomes "_", as a file name cannot hold a path separator. */
const plainOrUnderscore = ch => {
	const plain = PLAIN_PUNCTUATION[ch] ?? ch.normalize('NFKC');
	return UNSUPPORTED.test(plain) || /[/\\]/.test(plain) ? '_' : plain;
};

/** A filename the report can write. Each run of unsupported characters without a plain form
 * becomes one "_": "驯鹿 herd.mp4" → "_ herd.mp4". */
export const reportSafeFilename = name =>
	String(name ?? '')
		.normalize('NFC')
		.replace(UNSUPPORTED_RUNS, run =>
			[...run].map(plainOrUnderscore).join('').replace(/_+/g, '_')
		);

/** The same file under a report-safe name; the file itself when its name is already safe. */
export const withReportSafeName = file => {
	const name = reportSafeFilename(file.name);
	return name === file.name
		? file
		: new File([file], name, { type: file.type, lastModified: file.lastModified });
};
